import Add16 from '@carbon/icons-react/es/add/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import Save16 from '@carbon/icons-react/es/save/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Tooltip } from 'antd';
import { Dictionary } from 'lodash';
import moment from 'moment';
import React, { FC, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User } from 'api/models';
import { RateCardLite, RateCardVersion } from 'api/rate-cards';
import { Button } from 'components/atoms/button';
import { Loading } from 'components/atoms/loading';
import { RelativeTime } from 'components/atoms/relative-time';
import { Caption01 } from 'components/styles';
import { PermissionsContext } from 'contexts/permissions';
import { fullName } from 'utils/full-name';

const FooterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  button {
    margin-left: var(--spacing-05);
  }
`;

const StyledCreationMeta = styled.div`
  ${Caption01};
  display: flex;
  align-items: center;
  flex: 1;
  height: var(--height-lg);
`;

const RedTrashCan = styled(TrashCan16)`
  color: var(--red-60);
`;

interface Props {
  canSubmit: boolean;
  userLookup: Dictionary<User>;
  selectedRateCard: RateCardLite | undefined;
  selectedRateCardVersion: RateCardVersion;
  isCurrentRateCardVersionReadOnly: boolean;
  isEditMode: boolean;
  enableEditMode: () => void;
  disableEditMode: () => void;
  onCreateNewRateCard: () => Promise<void>;
  isRateCardCreationInProgress: boolean;
  onCreateNewRateCardVersion: () => void;
  isRateCardVersionCreationInProgress: boolean;
  onUpdateRateCardVersion: () => void;
  isRateCardVersionUpdationInProgress: boolean;
  onDeleteRateCardVersion: () => Promise<void>;
  isRateCardVersionDeletionInProgress: boolean;
  onClose: () => void;
}

export const RateCardsDrawerFooter: FC<Props> = ({
  canSubmit,
  userLookup,
  selectedRateCard,
  selectedRateCardVersion,
  isCurrentRateCardVersionReadOnly,
  isEditMode,
  enableEditMode,
  disableEditMode,
  onClose,
  onCreateNewRateCard,
  isRateCardCreationInProgress,
  onCreateNewRateCardVersion,
  isRateCardVersionCreationInProgress,
  onUpdateRateCardVersion,
  isRateCardVersionUpdationInProgress,
  onDeleteRateCardVersion,
  isRateCardVersionDeletionInProgress,
}) => {
  const { canCreate, canWrite, canDelete } = useContext(PermissionsContext);

  const creator =
    selectedRateCardVersion.updatedBy != null
      ? userLookup[selectedRateCardVersion.updatedBy]
      : undefined;

  return (
    <FooterWrapper>
      {creator != null && selectedRateCardVersion.rateCardVersionId > 0 && (
        <StyledCreationMeta>
          <FormattedMessage
            id="settings.rate_cards.drawer.footer.creation_meta"
            values={{
              user: fullName(creator),
              time: (
                <RelativeTime
                  time={moment(selectedRateCardVersion.updatedAt)}
                />
              ),
            }}
          />
        </StyledCreationMeta>
      )}
      {!isCurrentRateCardVersionReadOnly &&
        (selectedRateCard == null ||
        selectedRateCardVersion.rateCardVersionId < 0 ? (
          canCreate && (
            <>
              <Button onClick={onClose}>
                <FormattedMessage id="cancel" />
              </Button>
              <Button
                disabled={
                  isRateCardCreationInProgress ||
                  isRateCardVersionCreationInProgress ||
                  !canSubmit
                }
                icon={<Add16 />}
                iconPlacement="right"
                type="primary"
                loading={
                  selectedRateCard == null
                    ? isRateCardCreationInProgress
                    : isRateCardVersionCreationInProgress
                }
                onClick={() => {
                  if (selectedRateCard == null) {
                    onCreateNewRateCard();
                  } else {
                    onCreateNewRateCardVersion();
                  }
                }}
              >
                <FormattedMessage id="create" />
              </Button>
            </>
          )
        ) : !isEditMode ? (
          <>
            {canDelete && (
              <Tooltip
                title={
                  <FormattedMessage id="settings.rate_cards.drawer.footer.delete_version.tooltip" />
                }
              >
                <Button
                  type="text"
                  icon={
                    isRateCardVersionDeletionInProgress ? (
                      <Loading />
                    ) : (
                      <RedTrashCan />
                    )
                  }
                  disabled={isRateCardVersionDeletionInProgress}
                  onClick={onDeleteRateCardVersion}
                />
              </Tooltip>
            )}
            {canWrite && (
              <Button
                disabled={isRateCardVersionDeletionInProgress || !canSubmit}
                icon={<Edit16 />}
                iconPlacement="right"
                type="primary"
                onClick={enableEditMode}
              >
                <FormattedMessage id="Edit" />
              </Button>
            )}
          </>
        ) : (
          canWrite && (
            <>
              <Button onClick={disableEditMode}>
                <FormattedMessage id="cancel" />
              </Button>
              <Button
                disabled={isRateCardVersionUpdationInProgress || !canSubmit}
                icon={<Save16 />}
                iconPlacement="right"
                type="primary"
                loading={isRateCardVersionUpdationInProgress}
                onClick={onUpdateRateCardVersion}
              >
                <FormattedMessage id="save" />
              </Button>
            </>
          )
        ))}
    </FooterWrapper>
  );
};
